import React, { useState } from "react";
import { Amplify, API } from "aws-amplify";
import {
  Authenticator,
  TextField,
  Alert,
  Divider,
  Badge,
} from "@aws-amplify/ui-react";
import { Box, Flex, Card } from "theme-ui";
import {
  Heading,
  Button as LumosButton,
  useNotify,
} from "@loanpal/lumos__core";
import NavBar from "../../shared/nav-bar";
import { formFields, components } from "../../utils/authentication";
import Loading from "../../utils/loading";
import awsExports from "../../aws-exports";
import "@aws-amplify/ui-react/styles.css";

Amplify.configure(awsExports);

const STEPS = [
  "Organization",
  "Request details",
  "Create QuickLink",
];

function StepPill({ index, label, active, done }) {
  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: 2,
        opacity: active || done ? 1 : 0.55,
      }}
    >
      <Flex
        sx={{
          width: 28,
          height: 28,
          borderRadius: "50%",
          alignItems: "center",
          justifyContent: "center",
          bg: done || active ? "#472BA4" : "#d7d7d7",
          color: "white",
          fontSize: 1,
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {index + 1}
      </Flex>
      <Box
        sx={{
          fontSize: 1,
          fontWeight: active ? 700 : 500,
          color: active ? "#323232" : "#6b6b6b",
        }}
      >
        {label}
      </Box>
    </Flex>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <Flex
      sx={{
        gap: 3,
        py: 2,
        borderBottom: "1px solid #eee",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ width: 180, flexShrink: 0, fontWeight: 600, fontSize: 1, color: "#5c5c5c" }}>
        {label}
      </Box>
      <Box sx={{ fontSize: 1, wordBreak: "break-word", flex: 1 }}>
        {typeof value === "string" && value.startsWith("http") ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          String(value)
        )}
      </Box>
    </Flex>
  );
}

function ExistingList({ title, items, linkPrefix }) {
  if (!items?.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: 2 }}>
        {title}
      </Heading>
      <Box as="ul" sx={{ m: 0, pl: 3 }}>
        {items.map((item) => (
          <Box as="li" key={item.id} sx={{ mb: 1, fontSize: 1 }}>
            <a href={`${linkPrefix}${item.id}`}>
              {item.accountName || item.partnerName || item.id}
            </a>
            {" · "}
            <Badge size="small">{item.status || "—"}</Badge>
            {item.category ? ` · ${item.category}` : null}
            {item.clientId ? ` · ${item.clientId}` : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const CreateQuicklink = () => {
  const { addSnack } = useNotify();
  const [activeStep, setActiveStep] = useState(0);
  const [salesforceId, setSalesforceId] = useState("");
  const [loading, setLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupResult, setLookupResult] = useState(null);

  const organization = lookupResult?.organization || null;
  const existingRequests = lookupResult?.existingRequests || [];
  const existingSharedApps = lookupResult?.existingSharedApps || [];
  const alreadyExists = lookupResult?.alreadyExists || {
    request: false,
    sharedApp: false,
  };

  const runLookup = async () => {
    const id = String(salesforceId || "").trim();
    if (!id) {
      addSnack({ variant: "danger", message: "Enter a Salesforce Account Id." });
      return;
    }
    setLoading(true);
    setLookupError("");
    setLookupResult(null);
    try {
      const response = await API.post("developerApi", "/create-quicklink/lookup", {
        body: { salesforceId: id },
      });
      setLookupResult(response);
      if (response?.alreadyExists?.request || response?.alreadyExists?.sharedApp) {
        addSnack({
          variant: "warning",
          message: "This Salesforce Id already has a Request and/or QuickLink.",
        });
      } else {
        addSnack({ variant: "success", message: "Organization found." });
      }
    } catch (err) {
      console.log("create-quicklink lookup error", err);
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to look up Salesforce organization.";
      setLookupError(msg);
      // Surface partial existence data if the Lambda returned it with the error
      const body = err?.response?.data;
      if (body?.existingRequests || body?.salesforceId) {
        setLookupResult({
          salesforceId: body.salesforceId || id,
          organization: null,
          existingRequests: body.existingRequests || [],
          existingSharedApps: body.existingSharedApps || [],
          alreadyExists: {
            request: (body.existingRequests || []).length > 0,
            sharedApp: (body.existingSharedApps || []).length > 0,
          },
        });
      }
      addSnack({ variant: "danger", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Authenticator formFields={formFields} components={components} hideSignUp>
      {({ signOut }) => (
        <>
          {loading ? <Loading /> : null}
          <NavBar>
            <LumosButton onClick={signOut} style={styles.signOut}>
              Sign out
            </LumosButton>
          </NavBar>

          <Box sx={styles.page}>
            <Heading variant="h4" sx={{ mb: 2 }}>
              Create QuickLink
            </Heading>
            <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 4, maxWidth: 640 }}>
              Step-by-step wizard to create a Request, then a SharedApp / QuickLink.
            </Box>

            <Flex
              sx={{
                gap: [3, 4],
                flexWrap: "wrap",
                mb: 4,
                p: 3,
                bg: "white",
                borderRadius: 8,
                border: "1px solid #e5e5e5",
              }}
            >
              {STEPS.map((label, i) => (
                <React.Fragment key={label}>
                  <StepPill
                    index={i}
                    label={label}
                    active={i === activeStep}
                    done={i < activeStep}
                  />
                  {i < STEPS.length - 1 ? (
                    <Box
                      sx={{
                        display: ["none", "block"],
                        width: 40,
                        height: 2,
                        bg: i < activeStep ? "#472BA4" : "#e5e5e5",
                        alignSelf: "center",
                      }}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </Flex>

            {activeStep === 0 ? (
              <Card sx={styles.card}>
                <Heading variant="h5" as="h2" sx={{ mb: 2 }}>
                  Step 1 — Organization
                </Heading>
                <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 3 }}>
                  Enter the Salesforce Account Id. We look up organization details in
                  Databricks and check whether a Request or QuickLink already exists.
                </Box>

                <Flex
                  sx={{
                    gap: 3,
                    alignItems: "flex-end",
                    flexWrap: "wrap",
                    mb: 3,
                  }}
                >
                  <Box sx={{ flex: "1 1 280px", maxWidth: 420 }}>
                    <TextField
                      label="Salesforce Account Id"
                      size="small"
                      value={salesforceId}
                      onChange={(e) => setSalesforceId(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") runLookup();
                      }}
                      placeholder="001…"
                    />
                  </Box>
                  <LumosButton
                    type="button"
                    onClick={runLookup}
                    disabled={loading}
                  >
                    {loading ? "Looking up…" : "Look up organization"}
                  </LumosButton>
                </Flex>

                {lookupError ? (
                  <Alert variation="error" marginBottom="1rem">
                    {lookupError}
                  </Alert>
                ) : null}

                {alreadyExists.request || alreadyExists.sharedApp ? (
                  <Alert variation="warning" marginBottom="1rem">
                    {alreadyExists.request && alreadyExists.sharedApp
                      ? "A Request and a QuickLink already exist for this Salesforce Id / organization."
                      : alreadyExists.request
                        ? "A Request already exists for this Salesforce Id."
                        : "A QuickLink (SharedApp) already exists for this organization / client."}
                  </Alert>
                ) : null}

                {organization ? (
                  <Box sx={{ mt: 2 }}>
                    <Heading variant="h6" as="h3" sx={{ mb: 2, fontSize: 2 }}>
                      Organization details
                    </Heading>
                    <Box
                      sx={{
                        border: "1px solid #eee",
                        borderRadius: 8,
                        px: 3,
                        bg: "#fafafa",
                      }}
                    >
                      <InfoRow label="Account name" value={organization.accountName} />
                      <InfoRow label="Client Id" value={organization.clientId} />
                      <InfoRow
                        label="Organization Id"
                        value={organization.organizationId}
                      />
                      <InfoRow
                        label="Account owner email"
                        value={organization.accountOwnerEmail}
                      />
                      <InfoRow
                        label="Partner email"
                        value={organization.partnerEmail}
                      />
                      <InfoRow
                        label="Submitting user email"
                        value={organization.submittingUserEmail}
                      />
                      <InfoRow label="Salesforce" value={organization.salesforce} />
                      <InfoRow
                        label="Salesforce Id"
                        value={organization.salesforceId || salesforceId}
                      />
                    </Box>

                    <ExistingList
                      title="Existing requests"
                      items={existingRequests}
                      linkPrefix="/request/"
                    />
                    <ExistingList
                      title="Existing QuickLinks"
                      items={existingSharedApps}
                      linkPrefix="/edit/"
                    />

                    <Divider marginTop="1.5rem" marginBottom="1rem" />
                    <Flex sx={{ justifyContent: "flex-end", gap: 2 }}>
                      <LumosButton
                        type="button"
                        variant="primary"
                        disabled={!organization}
                        onClick={() => {
                          setActiveStep(1);
                          addSnack({
                            variant: "info",
                            message:
                              "Next steps (request details + QuickLink create) coming next.",
                          });
                        }}
                      >
                        Continue
                      </LumosButton>
                    </Flex>
                  </Box>
                ) : null}
              </Card>
            ) : null}

            {activeStep > 0 ? (
              <Card sx={styles.card}>
                <Heading variant="h5" as="h2" sx={{ mb: 2 }}>
                  Step {activeStep + 1} — {STEPS[activeStep]}
                </Heading>
                <Box sx={{ color: "#5c5c5c", fontSize: 1, mb: 3 }}>
                  This step will be wired next. Organization from step 1 is ready.
                </Box>
                {organization ? (
                  <Box sx={{ mb: 3, fontSize: 1 }}>
                    <strong>{organization.accountName}</strong>
                    {organization.clientId ? ` · ${organization.clientId}` : ""}
                  </Box>
                ) : null}
                <LumosButton type="button" variant="secondary" onClick={() => setActiveStep(0)}>
                  Back to Organization
                </LumosButton>
              </Card>
            ) : null}
          </Box>
        </>
      )}
    </Authenticator>
  );
};

const styles = {
  page: {
    maxWidth: 900,
    mx: "auto",
    px: [3, 4],
    py: 4,
  },
  card: {
    p: [3, 4],
    borderRadius: 8,
    border: "1px solid #e5e5e5",
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
    bg: "white",
  },
  signOut: {
    backgroundColor: "transparent",
    color: "white",
    border: "none",
  },
};

export default CreateQuicklink;
